/**
 * Copyright (C) 2026 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { GroundStationAvailability, GroundStationData, Reservation } from '../../shared/entities/haloNet';
import { Action, ActionCreators } from './actions';

export interface HaloNetGroundStationsState {
  groundStations: GroundStationData[];
  availability: GroundStationAvailability[];
  reservations: Reservation[];
  visibleStations: Record<string, boolean>;

  isRequestingGroundStations: boolean;
  isRequestingAvailability: boolean;
  isRequestingReservations: boolean;

  groundStationsError?: string;
  availabilityError?: string;
  reservationsError?: string;
}

export const initialHaloNetGroundStationsState: HaloNetGroundStationsState = {
  groundStations: [],
  availability: [],
  reservations: [],
  visibleStations: {},

  isRequestingGroundStations: false,
  isRequestingAvailability: false,
  isRequestingReservations: false,

  groundStationsError: undefined,
  availabilityError: undefined,
  reservationsError: undefined,
};

export default function haloNetGroundStationsReducer(
  state: HaloNetGroundStationsState = initialHaloNetGroundStationsState,
  action: Action,
): HaloNetGroundStationsState {
  let partialState: Partial<HaloNetGroundStationsState> | undefined;

  switch (action.type) {
    case ActionCreators.RequestHaloNetGroundStations.type:
      partialState = {
        groundStations: [],
        visibleStations: {},
        isRequestingGroundStations: true,
        groundStationsError: undefined,
      };
      break;

    case ActionCreators.ReceivedHaloNetGroundStationsSuccess.type: {
      const groundStations = action.payload.response || [];
      const visibleStations = (groundStations as GroundStationData[]).reduce((accumulator, groundStation) => {
        accumulator[groundStation.groundStationName] = state.visibleStations[groundStation.groundStationName] ?? false;
        return accumulator;
      }, {} as Record<string, boolean>);

      partialState = {
        groundStations,
        visibleStations,
        isRequestingGroundStations: false,
        groundStationsError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedHaloNetGroundStationsError.type:
      partialState = {
        isRequestingGroundStations: false,
        groundStationsError: action.payload?.message,
      };
      break;

    case ActionCreators.RequestHaloNetGroundStationAvailability.type:
      partialState = {
        availability: [],
        isRequestingAvailability: true,
        availabilityError: undefined,
      };
      break;

    case ActionCreators.ReceivedHaloNetGroundStationAvailabilitySuccess.type:
      partialState = {
        availability: action.payload.response || [],
        isRequestingAvailability: false,
        availabilityError: undefined,
      };
      break;

    case ActionCreators.ReceivedHaloNetGroundStationAvailabilityError.type:
      partialState = {
        isRequestingAvailability: false,
        availabilityError: action.payload?.message,
      };
      break;

    case ActionCreators.RequestHaloNetGroundStationReservations.type:
      partialState = {
        reservations: [],
        isRequestingReservations: true,
        reservationsError: undefined,
      };
      break;

    case ActionCreators.ReceivedHaloNetGroundStationReservationsSuccess.type:
      partialState = {
        reservations: action.payload.response || [],
        isRequestingReservations: false,
        reservationsError: undefined,
      };
      break;

    case ActionCreators.ReceivedHaloNetGroundStationReservationsError.type:
      partialState = {
        isRequestingReservations: false,
        reservationsError: action.payload?.message,
      };
      break;

    case ActionCreators.ToggleHaloNetGroundStationVisibility.type: {
      const siteDesignation = action.payload as string;
      partialState = {
        visibleStations: {
          ...state.visibleStations,
          [siteDesignation]: !state.visibleStations[siteDesignation],
        },
      };
      break;
    }

    case ActionCreators.ToggleAllHaloNetGroundStationsVisibility.type: {
      const isVisible = action.payload as boolean;
      const visibleStations = Object.keys(state.visibleStations).reduce((accumulator, key) => {
        accumulator[key] = isVisible;
        return accumulator;
      }, {} as Record<string, boolean>);

      partialState = {
        visibleStations,
      };
      break;
    }

    default:
      return state;
  }

  return { ...state, ...partialState };
}
